import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Modal as UIModal } from "@/components/ui/Modal";
import { formatRelativeTime, formatAbsoluteDate } from "./Shared";
import type { FieldRejectionInfo } from "@/lib/api/review";
import type { SupportedLocale } from "@chinese-vendor/i18n";

export interface DetailFieldReasonModalProps {
  visible: boolean;
  onDismiss: () => void;
  data?: FieldRejectionInfo;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  locale: SupportedLocale;
}

export function DetailFieldReasonModal({
  visible,
  onDismiss,
  data,
  t,
  locale,
}: DetailFieldReasonModalProps) {
  return (
    <UIModal
      visible={visible}
      onDismiss={onDismiss}
      title="review.review.fieldState.reasonModal.title"
      closeOnBackdrop
      useScrollView
    >
      {/* REJECTED BY */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.rejectedBy")}
        </AppText>
        <View className="gap-2">
          <AppText weight="medium" className="text-dark-text-50">
            {data?.rejectedBy?.name ?? "—"}
          </AppText>
          {data?.rejectedBy?.role && <Badge variant="accent-blue" label={data.rejectedBy.role} />}
        </View>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* DATE REJECTED */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.dateRejected")}
        </AppText>
        {data?.rejectedAt ? (
          <View className="gap-1">
            <AppText weight="bold" className="text-dark-text-50">
              {formatRelativeTime(data.rejectedAt, locale, t)}
            </AppText>
            <AppText className="text-sm text-supporting-text">
              {formatAbsoluteDate(data.rejectedAt, locale)}
            </AppText>
          </View>
        ) : (
          <AppText weight="medium" className="text-dark-text-50">
            —
          </AppText>
        )}
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* REASON */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.reason")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.reason ?? "—"}
        </AppText>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* INTERNAL NOTE */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.internalNote")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.note ?? "—"}
        </AppText>
      </View>
    </UIModal>
  );
}
